import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { EmbedRequest } from '../interfaces/embed-request';
import { AuthenticationOidcService } from './authentication-oidc-service';
import { Observable, retry, switchMap, throwError, timer } from 'rxjs';
import { getCurrentProviderType } from '../utils/provider-type-information-utils';
import { FromProviderType } from '../interfaces/from-type-enum';

@Injectable({
  providedIn: 'root',
})
export class PowerbiService {
  private readonly http = inject(HttpClient);
  private readonly authenticationOidcService = inject(AuthenticationOidcService);

  private readonly apiEmbedTokenPath = `/powerbi/embed-token`;

  /**
   * Generates an embed token for a Power BI report, sending the user's token in the Authorization header.
   * @param groupId Power BI group ID
   * @param reportId Power BI report ID
   * @returns Observable with the embed token response
   */
  generateEmbedToken(groupId: string, reportId: string): Observable<any> {
    if (!groupId || !reportId) {
      return throwError(() => new Error('groupId and reportId are required'));
    }

    var providerType = getCurrentProviderType();
    if (!providerType) {
      return throwError(() => new Error('Provider type is not set.'));
    }

    const apiUrl = this.getApiUrl(providerType);

    return this.authenticationOidcService.getUserInfo().pipe(
      switchMap((userInfo) => {
        const body: EmbedRequest = {
          groupId,
          reportId,
          email: userInfo.email || '',
        };
        return this.authenticationOidcService.getBearerToken().pipe(
          switchMap((token) => {
            const headers = token
              ? new HttpHeaders({ Authorization: `Bearer ${token}` })
              : undefined;
            return this.http.post(apiUrl, body, { headers }).pipe(
              retry({
                count: 3,
                delay: (error, retryCount) => timer(retryCount * 1000),
              }),
            );
          }),
        );
      }),
    );
  }

  /**
   * Returns the API URL for the given provider type.
   * @param providerType The provider type
   * @returns The API URL as a string
   */
  private getApiUrl(providerType: FromProviderType): string {
    switch (providerType) {
      case FromProviderType.IMPERIA:
        return environment.API_IMPERIA_URL + this.apiEmbedTokenPath;
      case FromProviderType.SUITEEDGE:
        return environment.API_SUITEEDGE_URL + this.apiEmbedTokenPath;
      default:
        throw new Error('Unsupported provider type');
    }
  }
}
